/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

const RulesPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Define the shitmove categories and types
  const shitmoveRules = [
    {
      category: 'Serious Issues (Count As Penalties)',
      description: 'These are overtly disruptive techniques that count as penalties. 3 of the same type or 5 total will lose the debate.',
      color: theme.palette.error.main,
      types: [
        {
          name: 'Ad Hominem',
          code: 'ad_hominem',
          description: 'Personal attacks that derail focus from the argument',
          example: 'You\'re too uneducated to understand this topic.',
          better: 'Let\'s focus on the evidence presented rather than personal characteristics.',
        },
        {
          name: 'Textual Intimidation',
          code: 'textual_intimidation',
          description: 'Using formatting (like ALL CAPS) to intimidate rather than argue',
          example: 'This is ABSOLUTELY RIDICULOUS and ANYONE who believes this is DELUSIONAL!',
          better: 'I strongly disagree with this position for the following reasons...',
        },
        {
          name: 'Grammar Policing',
          code: 'grammar_policing',
          description: 'Avoiding the argument by focusing on trivial errors',
          example: 'Your use of "their" instead of "there" invalidates your entire argument.',
          better: 'Setting aside the typo, I\'d like to address the substance of your point...',
        },
        {
          name: 'Sarcasm Overload',
          code: 'sarcasm_overload',
          description: 'Using excessive mockery that poisons discourse',
          example: 'Oh sure, THAT makes perfect sense (eye roll).',
          better: 'I disagree with that point for the following reasons...',
        },
      ]
    },
    {
      category: 'Issues Requiring Reformulation (No Penalty)',
      description: 'These involve flawed reasoning that can be corrected without penalty. They require reformulation but don\'t count against your score.',
      color: theme.palette.warning.main,
      types: [
        {
          name: 'Strawman',
          code: 'strawman',
          description: 'Misrepresenting someone\'s argument to create a weaker version to attack',
          example: 'So you\'re saying we should completely abandon all traditional values?',
          better: 'Let me make sure I understand your position correctly. Are you suggesting...',
        },
        {
          name: 'Word Twisting',
          code: 'word_twisting',
          description: 'Misrepresenting someone\'s words to create a weaker argument to attack',
          example: 'You mentioned "reform" which clearly means you want to destroy the system.',
          better: 'When you mentioned reform, could you clarify what specific changes you\'re advocating for?',
        },
        {
          name: 'Whataboutism',
          code: 'whataboutism',
          description: 'Deflecting from the current point by bringing up unrelated issues',
          example: 'Why focus on this issue when there are children starving?',
          better: 'I understand there are other important issues, but let\'s address the specific point we\'re discussing.',
        },
        {
          name: 'Gish Gallop',
          code: 'gish_gallop',
          description: 'Overwhelming with too many points or links to prevent meaningful response',
          example: 'Here are 15 different arguments and 20 links that prove my point.',
          better: 'Let me focus on the strongest evidence for my position, which is...',
        },
        {
          name: 'Shifting Goalposts',
          code: 'shifting_goalposts',
          description: 'Changing criteria of an argument when original criteria are met',
          example: 'That study doesn\'t count because it wasn\'t conducted in our country.',
          better: 'Thank you for that evidence. It helps me understand your position better.',
        },
        {
          name: 'Quote Sniping',
          code: 'quote_sniping',
          description: 'Distorting quotes out of context to mislead deliberately',
          example: 'You said "I support reform" which proves you\'re against tradition.',
          better: 'In context, it seems your support for reform is specifically about...',
        },
        {
          name: 'Sealioning',
          code: 'sealioning',
          description: 'Relentless questioning that exhausts rather than engages',
          example: 'Can you prove that? What about this? And this? And this source? What about...',
          better: 'I have a specific concern about your main point, which is...',
        },
        {
          name: 'Feigning Confusion',
          code: 'feigning_confusion',
          description: 'Pretending not to understand to stall or derail discussion',
          example: 'I just don\'t get what you\'re trying to say at all. None of this makes sense.',
          better: 'I\'m unclear about a specific part of your argument. Could you explain...',
        },
        {
          name: 'Overgeneralizations',
          code: 'overgeneralizations',
          description: 'Sweeping statements that lack precision but can be refined',
          example: 'Everyone knows this policy has failed.',
          better: 'Several studies have shown this policy has not achieved its stated goals.',
        },
        {
          name: 'False Dichotomy',
          code: 'false_dichotomy',
          description: 'Forcing an "either-or" choice to oversimplify',
          example: 'Either we accept this proposal completely or reject it entirely.',
          better: 'There are several aspects of this proposal we could consider separately.',
        },
        {
          name: 'Appeal to Authority',
          code: 'appeal_to_authority',
          description: 'Using irrelevant citations or authorities that can be replaced',
          example: 'As a famous celebrity once said, this approach is wrong.',
          better: 'According to experts in this field with relevant credentials...',
        },
        {
          name: 'Appeal to Emotion',
          code: 'appeal_to_emotion',
          description: 'Using emotional appeals that bypass logic but can be rephrased',
          example: 'Think of how sad this makes people feel!',
          better: 'There are concerning impacts we should consider, including...',
        },
        {
          name: 'Cherry-Picking',
          code: 'cherry_picking',
          description: 'Using selective evidence that is misleading but correctable',
          example: 'This one study from 1985 proves my point.',
          better: 'Looking at the broader research landscape, including recent studies...',
        },
        {
          name: 'Unsubstantiated Claims',
          code: 'unsubstantiated_claims',
          description: 'Making bold claims without evidence or substance',
          example: 'This will obviously lead to disaster.',
          better: 'Based on these specific factors, there\'s reason to be concerned about...',
        },
        {
          name: 'Selective Skepticism',
          code: 'selective_skepticism',
          description: 'Applying doubt unevenly based on bias',
          example: 'I trust these studies that support my view but reject all others.',
          better: 'Applying consistent standards of evidence, we should consider...',
        },
        {
          name: 'Circular Reasoning',
          code: 'circular_reasoning',
          description: 'Assuming the conclusion in the premise',
          example: 'This policy is good because it produces good outcomes.',
          better: 'This policy is beneficial because it leads to measurable improvements in...',
        },
      ]
    }
  ];

  // Debate rules section
  const debateRules = [
    {
      title: t('turnBasedStructure'),
      description: t('turnBasedDescription')
    },
    {
      title: t('jestersGauntletPenalties'),
      description: t('jestersGauntletDescription')
    },
    {
      title: t('flaggedMessages'),
      description: t('flaggedMessagesDescription')
    },
    {
      title: t('reformulationProcess'),
      description: t('reformulationDescription')
    },
    {
      title: t('conceding'),
      description: t('concedingDescription')
    },
    {
      title: t('inactiveDebates'),
      description: t('inactiveDebatesDescription')
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('rulesAndGuidelines')}
      </Typography>
      
      <Typography variant="subtitle1" paragraph>
        Understand how the Jester's Gauntlet™ system works to keep debates honorable and productive.
      </Typography>

      {/* Introduction */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom>
          {t('introductionToHonorableDebate')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('productiveDebateRelies')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('aiModeratorAnalyzes')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2, mb: 3 }}>
          <Chip 
            icon={<WarningIcon />} 
            label={t('seriousIssuesPenalties')} 
            color="error" 
            variant="outlined" 
          />
          <Chip 
            icon={<InfoIcon />} 
            label={t('issuesRequiringReformulation')} 
            color="warning" 
            variant="outlined" 
          />
        </Box>
        <Typography variant="body2">
          {t('whenYourMessageContains')}
        </Typography>
      </Paper>

      {/* Main content sections */}
      <Grid container spacing={4}>
        {/* Shitmove Rules Section */}
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
            {t('debateMisconductTypes')}
          </Typography>
          {shitmoveRules.map((category, catIndex) => (
            <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }} key={catIndex}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" component="h2">
                  {category.category === 'Serious Issues (Count As Penalties)' ? t('seriousIssuesCountAsPenalties') : category.category}
                </Typography>
                <Chip 
                  label={catIndex === 0 ? t('countsAgainstYou') : t('requiresReformulation')} 
                  color={catIndex === 0 ? "error" : "warning"}
                  size="small"
                  sx={{ ml: 2 }}
                />
              </Box>
              
              <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                {catIndex === 0 ? t('theseAreOvertlyDisruptive') : category.description}
              </Typography>
              
              {category.types.map((type, typeIndex) => (
                <Accordion key={typeIndex} sx={{ mb: 1 }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      borderLeft: `4px solid ${category.color}`,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {type.name}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" paragraph>
                      {type.description}
                    </Typography>
                    <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 1 }}>
                      <Typography variant="subtitle2" color="error" gutterBottom>
                        Example of {type.name}:
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        "{type.example}"
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(76,175,80,0.08)', borderRadius: 1 }}>
                      <Typography variant="subtitle2" color="success.main" gutterBottom>
                        Better Approach:
                      </Typography>
                      <Typography variant="body2">
                        "{type.better}"
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Internal code: {type.code}
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Paper>
          ))}
        </Grid>

        {/* Debate Rules Section */}
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
            {t('debateRules')}
          </Typography>
          <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography variant="h5" gutterBottom>
              {t('debateRulesSummary')}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {debateRules.map((rule, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {rule.title}
                      </Typography>
                      <Typography variant="body2">
                        {rule.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default RulesPage; 